'use client';

import {
  useId,
  useState,
  type InputHTMLAttributes,
  type MouseEventHandler,
  type ReactNode,
} from 'react';
import InputError from './InputError';
import InputLabel from './InputLabel';
import { type TooltipProps } from './Tooltip';
import { Field } from '~/components/ui/field';
import { cn } from '~/lib/utils';

type InputAttributes = InputHTMLAttributes<HTMLInputElement>;
type InputSize = 'sm' | 'md' | 'lg';

const sizeClasses = {
  sm: 'h-8 text-xs leading-none',
  md: 'h-9 text-sm leading-none',
  lg: 'h-10 text-base leading-none',
};

const inputPaddingClasses = {
  sm: 'px-3',
  md: 'px-3',
  lg: 'px-3',
};

export type TextInputProps = {
  label?: React.ReactNode;
  placeholder?: string;
  value?: InputAttributes['value'];
  onChange?: InputAttributes['onChange'];
  readOnly?: boolean;
  inputProps?: Omit<InputAttributes, 'value' | 'onChange' | 'readOnly'>;
  className?: string;
  onClick?: MouseEventHandler<HTMLDivElement>;
  tooltip?: React.ReactNode;
  tooltipProps?: Partial<TooltipProps>;
  disabled?: boolean;
  error?: string;
  autoFocus?: boolean;
  renderStart?: ReactNode;
  renderEnd?: ReactNode;
  size?: InputSize;
};

export default function TextInput({
  label,
  placeholder,
  value,
  onChange,
  inputProps,
  className,
  onClick,
  readOnly,
  tooltip,
  disabled,
  tooltipProps,
  error,
  autoFocus,
  renderStart,
  renderEnd,
  size = 'md',
}: TextInputProps) {
  const sizeClass = sizeClasses[size];
  const inputPadding = inputPaddingClasses[size];
  const [isFocused, setIsFocused] = useState(false);
  const inputId = useId();

  return (
    <Field
      className={cn('font-medium', className)}
      onClick={onClick}
      data-invalid={error ? true : undefined}
      data-disabled={disabled ? true : undefined}
    >
      {typeof label === 'string' ? (
        <InputLabel label={label} tooltip={tooltip} tooltipProps={tooltipProps} htmlFor={inputId} />
      ) : (
        label
      )}

      <div
        className={cn(
          'group flex rounded-md',
          !disabled && 'focus-within:ring-ring/50 focus-within:ring-[3px]',
          !disabled && isFocused && 'ring-ring/50 ring-[3px]',
        )}
      >
        {renderStart && (
          <div
            className={cn(
              'border-input bg-background flex items-center justify-center rounded-l-md border border-r-0 pl-2',
              sizeClass,
              disabled && 'bg-muted/50 text-muted-foreground cursor-not-allowed opacity-70',
            )}
          >
            {renderStart}
          </div>
        )}

        <input
          id={inputId}
          disabled={disabled}
          type="text"
          placeholder={placeholder}
          readOnly={readOnly}
          aria-invalid={error ? true : undefined}
          {...inputProps}
          className={cn(
            'border-input bg-background w-full flex-1 border shadow-xs focus:outline-hidden',
            inputPadding,
            renderStart ? 'border-l-0' : 'rounded-l-md',
            renderEnd ? 'border-r-0' : 'rounded-r-md',
            sizeClass,
            disabled &&
              'bg-muted/50 text-muted-foreground cursor-not-allowed opacity-70 shadow-none',
            inputProps?.className,
          )}
          value={value}
          onChange={onChange}
          autoFocus={autoFocus}
          onFocus={(e) => {
            if (disabled) return;
            setIsFocused(true);
            inputProps?.onFocus?.(e);
          }}
          onBlur={(e) => {
            setIsFocused(false);
            inputProps?.onBlur?.(e);
          }}
        />

        {renderEnd && (
          <div
            className={cn(
              'border-input bg-background flex items-center justify-center rounded-r-md border border-l-0 pr-2 transition-opacity duration-200',
              sizeClass,
              disabled && 'bg-muted/50 text-muted-foreground cursor-not-allowed opacity-70',
            )}
          >
            {renderEnd}
          </div>
        )}
      </div>

      {error ? <InputError error={error} /> : null}
    </Field>
  );
}
